import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useUserTrips } from '../../hooks/useUserTrips';
import { logOut } from '../../services/auth';
import { TripMeta } from '../../types/trip';
import { parlorScreen, useDesign } from '../../designs';
import DesignButton from '../../components/ui/DesignButton';

const d = parlorScreen.tokens;

function TripCard({ trip }: { trip: TripMeta }) {
  const navigate = useNavigate();
  const { typography } = useDesign();
  const displayFont = `'${typography.displayFont}', serif`;
  const fs = typography.fontSize;

  const formatDates = (start: string, end: string) => {
    if (!start || !end) return '';
    const s = new Date(start + 'T00:00:00');
    const e = new Date(end + 'T00:00:00');
    const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    return `${s.toLocaleDateString('en-US', opts)} – ${e.toLocaleDateString('en-US', opts)}`;
  };

  const isShared = trip.phase === 'shared';

  return (
    <div
      onClick={() => navigate(`/trip/${trip.tripId}`)}
      style={{
        backgroundColor: d.bgSurface,
        border: `1px solid rgba(200,146,58,0.12)`,
        borderRadius: '2px',
        padding: '16px 18px',
        marginBottom: '12px',
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
        <h3 style={{
          fontFamily: displayFont, fontSize: '20px', fontWeight: 700,
          color: d.textPrimary, margin: 0, lineHeight: 1.2,
        }}>
          {trip.tripName}
        </h3>
        <span style={{
          fontFamily: "'Raleway', sans-serif", fontSize: fs.sectionHeader, fontWeight: 700,
          letterSpacing: '1.5px', textTransform: 'uppercase',
          padding: '3px 8px', borderRadius: '2px', flexShrink: 0, marginLeft: '8px',
          backgroundColor: isShared ? 'rgba(42,144,104,0.12)' : 'rgba(200,146,58,0.12)',
          color: isShared ? d.confirmed : d.accent,
        }}>
          {isShared ? 'SHARED' : 'BUILD'}
        </span>
      </div>

      <p style={{
        fontFamily: displayFont, fontSize: fs.bodyInput, fontWeight: 400,
        fontStyle: 'italic', color: d.accent, margin: '0 0 4px',
      }}>
        {trip.destination}
      </p>

      {trip.startDate && trip.endDate && (
        <p style={{
          fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300,
          color: d.textSecondary, margin: 0,
        }}>
          {formatDates(trip.startDate, trip.endDate)}
        </p>
      )}
    </div>
  );
}

export default function DashboardScreen() {
  const { typography } = useDesign();
  const displayFont = `'${typography.displayFont}', serif`;
  const fs = typography.fontSize;
  const { user } = useAuth();
  const { data: trips, isLoading } = useUserTrips(user?.uid);
  const navigate = useNavigate();

  // Safety: if an anonymous user somehow reaches the dashboard
  // (e.g., stale auth from share-link testing), sign them out.
  React.useEffect(() => {
    if (user?.isAnonymous) {
      logOut().then(() => navigate('/login', { replace: true }));
    }
  }, [user, navigate]);

  const handleLogout = async () => {
    await logOut();
    navigate('/login');
  };

  if (isLoading) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        height: '100vh', backgroundColor: d.bgPrimary,
        fontFamily: displayFont, fontSize: fs.screenHeader, fontWeight: 700,
        color: d.textPrimary,
      }}>
        KREWE
      </div>
    );
  }

  // Empty state
  if (trips.length === 0) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', minHeight: '100vh', padding: '32px',
        backgroundColor: d.bgPrimary, textAlign: 'center',
      }}>
        <h1 style={{
          fontFamily: displayFont, fontSize: fs.screenHeader, fontWeight: 700,
          color: d.textPrimary, margin: '0 0 12px', lineHeight: 1.2,
        }}>
          Plan something worth<br />showing up for
        </h1>

        <p style={{
          fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyInput, fontWeight: 300,
          color: d.textSecondary, lineHeight: 1.6, maxWidth: '300px',
          margin: '0 0 32px',
        }}>
          Build the itinerary, invite the crew, and keep everyone on the same page.
        </p>

        <DesignButton tier={2} onClick={() => navigate('/new-trip')}>
          ◆ NEW TRIP ◆
        </DesignButton>

        <button onClick={handleLogout} type="button" style={{
          background: 'none', border: 'none', cursor: 'pointer', marginTop: '32px',
          fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300,
          color: d.accent,
        }}>
          Sign out
        </button>
      </div>
    );
  }

  // Trip list
  return (
    <div style={{
      maxWidth: '480px', margin: '0 auto', padding: '32px',
      minHeight: '100vh', backgroundColor: d.bgPrimary,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <h1 style={{
          fontFamily: displayFont, fontSize: fs.screenHeader, fontWeight: 700,
          color: d.textPrimary, margin: 0,
        }}>
          KREWE
        </h1>
        <button onClick={handleLogout} type="button" style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300,
          color: d.accent,
        }}>
          Sign out
        </button>
      </div>

      <h2 style={{
        fontFamily: "'Raleway', sans-serif", fontSize: fs.sectionHeader, fontWeight: 700,
        letterSpacing: '3px', textTransform: 'uppercase',
        color: d.textSecondary, marginBottom: '16px',
      }}>
        Your Trips
      </h2>

      {trips.map((trip) => (
        <TripCard key={trip.tripId} trip={trip} />
      ))}

      {/* New Trip CTA */}
      <div style={{ marginTop: '24px' }}>
        <DesignButton tier={2} fullWidth onClick={() => navigate('/new-trip')}>
          ◆ NEW TRIP ◆
        </DesignButton>
      </div>
    </div>
  );
}
